<template>
  <div class="container-fluid page-container">
    <div class="row page-header__container mb-4 pb-3">
      <page-heading :class="'col align-items-center'" :title="pageTitle" />
    </div>
    <spinner v-if="busy" />
    <ban-edit-profile v-if="isBannedFromEditing && !busy" />
    <div v-else>
      <b-row v-if="empty" class="vh-70">
        <b-col class="col-sm-7 m-auto">
          <b-card :body-class="['shadow', 'border', 'rounded', 'py-5', 'text-center']">
            <b-card-text>
              <h2 class="mb-0 h3 px-3">
                {{ $t('You have no required accreditations at the moment') }}.
              </h2>
              <hr class="w-50">
              <router-link v-if="showSettings" :class="'text-link  text-link-reset'"
                           :to="{name: 'lead-selection-&-coverage-tab', params: {step: 'lead-types'}}"
              >
                {{ $t('Setup lead types to add Accreditations') }}
              </router-link>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>
      <div v-else class="row">
        <div v-if="!busy && showList" class="col-12">
          <div class="card col-12 bg-white shadow rounded border p-2 mb-3">
            <div class="d-flex justify-content-end">
              <div class="d-inline-block text-center submit-container mr-2">
                <b-button
                  :variant="'primary'"
                  size="sm"
                  :disabled="disableSave"
                  :class="['btn-fixed', 'animated-submit', submitClassObject, {'disabled': disableSave, 'animate': animateSubmitButton}]"
                  @click="onSubmit"
                >
                  <span
                    v-b-tooltip.hover
                    class="d-flex justify-content-center w-100 stretched-link"
                    :title="disableSave ? $t('You have no changes in this page to save'): ''"
                  >{{ $t('Save') }}</span>
                </b-button>
              </div>
            </div>
          </div>
        </div>
        <div :class="['col', 'col-12', 'col-lg-8', (showList ? '' : 'd-none')]">
          <accreditations-list
            ref="accreditations"
            :show-lead-types-button="true"
            @selected="onSelected"
            @loaded="busy = false"
            @empty="empty=true"
            @change="disableSave = false"
          />
        </div>
        <b-card
          v-if="selectedAccreditation !== null"
          :header-class="'card-header__accent'"
          :class="'col h-100 position-sticky lead-types'"
        >
          <template v-slot:header>
            <b-col class="col-12 px-0 d-flex align-items-center">
              <b-button class="close position-absolute" variant="link" @click="onClose">
                <font-awesome-icon icon="times" />
              </b-button>
              <h2 class="text-center m-auto h4">
                {{ $t('Lead Types') }}
              </h2>
            </b-col>
          </template>
          <b-card-body class="p-4">
            <div class="text-center mb-3">
              <h2 class="h3 mb-2">
                {{ $t('Lead types available for accreditation') }}:
              </h2>
              <h3 class="h4 font-weight-500">
                {{ selectedAccreditation.question }}
              </h3>
            </div>
            <spinner v-if="busy" />
            <div v-else class="px-3">
              <h2 class="h4">
                {{ $t('Currently selected in your account') }}:
              </h2>
              <ul class="h5 font-weight-light list-unstyled worktype worktype-unavailable">
                <li v-for="{work_type: workType} in selectedWorkTypes" :key="workType.id" class="pl-3">
                  {{ workType.description }}
                </li>
              </ul>
              <div v-if="hasAvailableWorkTypes">
                <h2 class="h4">
                  {{ $t('Not selected but available') }}:
                </h2>
                <ul class="h5 font-weight-light list-unstyled worktype">
                  <li v-for="workType in availableWorkTypes" :key="workType.id" class="pl-3">
                    {{ workType.description }}
                    <b-button
                      v-if="!isLimitedAccount"
                      variant="empty"
                      size="sm"
                      class="text-success p-0"
                      @click="onWorkTypeSelected(workType)"
                    >
                      <font-awesome-icon
                        v-if="workType.id !== activeItemId"
                        class="mr-2 text-success"
                        :icon="['fas', 'plus-circle']"
                      />
                      <font-awesome-icon v-else class="mr-2 text-danger" :icon="['fas', 'minus-circle']" />
                    </b-button>
                  </li>
                </ul>
              </div>
              <b-overlay v-if="workTypeToAdd && savedSuccessfully === null" :show="savingWorkType">
                <b-form-group :label="$t('Choose a Coverage Area')">
                  <div class="row align-items-center mt-3">
                    <div class="col-6">
                      <b-form-select v-model="selectedCoverage">
                        <b-form-select-option v-for="area in coverageAreas" :key="area.id" :value="area">
                          {{ area.name }}
                        </b-form-select-option>
                      </b-form-select>
                    </div>
                  </div>
                </b-form-group>
                <div v-if="selectedCoverage !== null || newCoverageSelected === true">
                  <div class="align-items-center d-flex w-100">
                    <b-form-group>
                      <div class="align-items-center d-flex w-100">
                        <b-form-checkbox v-model="newLeadTypeConfirmation" class="float-left" />
                        <span class="ml-2 float-left small">{{ $t('I confirm that I would like to add this new Lead Type') }}.</span>
                      </div>
                    </b-form-group>
                  </div>
                  <div class="text-center mt-3 lead-types-save">
                    <b-button class="mr-1" variant="outline-secondary" size="sm" @click="workTypeToAdd = null">
                      Cancel
                    </b-button>
                    <b-button class="ml-1" variant="primary" size="sm" :disabled="!newLeadTypeConfirmation" @click="onAddWorkType">
                      {{ $t('Save') }}
                    </b-button>
                  </div>
                </div>
              </b-overlay>
              <div v-else-if="savedSuccessfully === true && selectedCoverage">
                <h2 class="h4">
                  {{ $t('leads.added', {coverageName: selectedCoverage.name}) }}
                </h2>
                <ul v-if="lastAdded">
                  <li>{{ lastAdded.description }}</li>
                </ul>
                <div class="text-center mt-3 lead-types-save">
                  <b-button
                    size="sm" :to="{name:'lead-selection-&-coverage-tab', params: {step: 'lead-types'}, query:{listing:selectedCoverage.id}}"
                  >
                    {{ $t('View in Leads Selection & Coverage') }}
                  </b-button>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
        <div v-if="!busy && showList" class="col-12">
          <div class="card col-12 bg-white shadow rounded border p-2">
            <div class="d-flex justify-content-end">
              <div class="d-inline-block text-center submit-container mr-2">
                <b-button
                  :variant="'primary'"
                  size="sm"
                  :disabled="disableSave"
                  :class="['btn-fixed', 'animated-submit', submitClassObject, {'disabled': disableSave, 'animate': animateSubmitButton}]"
                  @click="onSubmit"
                >
                  <span
                    v-b-tooltip.hover
                    class="d-flex justify-content-center w-100 stretched-link"
                    :title="disableSave ? $t('You have no changes in this page to save'): ''"
                  >{{ $t('Save') }}</span>
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AccreditationsList from '@/components/list/AccreditationsList';
import PageHeading from "@/components/control/PageHeading";
import Spinner from "@/components/animation/Spinner";
import BanEditProfile from "@/components/control/BanEditProfile";
import notification from "@/imports/mixins/notification";
import isBannedFromEditing from "@/imports/mixins/isBannedFromEditing";
import userDevice from "@/imports/mixins/userDevice";
import submitAnimationHandler from "@/imports/mixins/submitAnimationHandler";
import showSettings from "@/imports/mixins/companyAccount";
import isLimitedAccount from "@/imports/mixins/companyAccount";

export default {
  name: "Accreditations",
  components: {AccreditationsList, PageHeading, Spinner, BanEditProfile},
  mixins: [notification, isBannedFromEditing, userDevice, submitAnimationHandler, showSettings, isLimitedAccount],
  data() {
    return {
      pageTitle: this.$t("Accreditations"),
      selectedAccreditation: null,
      busy: true,
      disableSave: true,
      savedSuccessfully: null,
      loadingWorkTypes: false,
      selectedWorkTypes: [],
      availableWorkTypes: [],
      workTypeToAdd: null,
      coverageAreas: [],
      selectedCoverage: null,
      newCoverageSelected: false,
      newLeadTypeConfirmation: false,
      savingWorkType: false,
      activeItemId: null,
      empty: false,
      lastAdded: null
    };
  },
  computed: {
    showList() {
      return (!this.isMobile) || (this.selectedAccreditation === null);
    },
    hasAvailableWorkTypes: function() {
      return (Array.isArray(this.availableWorkTypes)) ? this.availableWorkTypes.length > 0 : false;
    },
  },
  beforeRouteLeave: function(to, from , next) {
    if (this.confirmDirtyForm()) {
      next(false);
    } else {
      next();
    }
  },
  methods: {
    onClose: function() {
      this.selectedAccreditation = null;
      this.activeItemId = null;
    },
    selectedItem: function(id) {
      this.activeItemId = id;
    },
    toggle: function(id) {
      let workType = this.workTypeToAdd;
      if (workType && workType.id === id) {
        this.workTypeToAdd = null;
      }
    },
    onSubmit: function() {
      this.disableSave = true;
      this.animateSubmitButton = true
      this.$refs.accreditations.onSubmit().then((success) => {
        this.notify(
          this.$bvToast,
          success ? this.$t('Your accreditations have been updated') : this.$t('There was an error saving your accreditations'),
          null,
          success ? 'success' : 'danger');
        this.enableSubmitAnimation(success);
      });
    },
    onSelected: function(accred) {
      this.selectedAccreditation = accred;
      this.loadingWorkTypes = true;
      this.coverageAreas = [];
      this.workTypeToAdd = null;
      this.savedSuccessfully = null;
      this.$api.coverage.get().then((response) => this.coverageAreas = response.data.data.map((area, index) => ({
        id: area.id,
        name: area.name && (area.name !== '' && area.name !== '0') ? area.name : `Coverage ${index+1}`
      })));
      this.$api.company.accreditationWorkTypes(accred.companyAccreditation.id).then((response) => {
        this.loadingWorkTypes = false;
        this.selectedWorkTypes = response.data.data.selectedWorkTypes;

        const selectedWorkTypes = this.selectedWorkTypes.map((workType) => workType.work_type.id);
        this.availableWorkTypes = response.data.data.availableWorkTypes.filter((workType) => !selectedWorkTypes.includes(parseInt(workType.id)));
      })
    },
    onWorkTypeSelected: function(workType) {
      if (this.workTypeToAdd === null || this.workTypeToAdd.id !== workType.id) {
        this.workTypeToAdd = workType;
        this.selectedItem(workType.id);
      } else {
        this.workTypeToAdd = null;
        this.selectedItem(null);
      }
      this.selectedCoverage = null;
      this.newLeadTypeConfirmation = false;
    },
    onAddWorkType: async function() {
      this.savingWorkType = true;
      this.savedSuccessfully = true;
      if (this.newCoverageSelected) {
        const newIndex = this.coverageAreas.length + 1;
        const {data} = await this.$api.coverage.create(`Coverage ${newIndex}`);
        this.selectedCoverage = data.data.coverage;
      }
      this.$api.coverage.getWorkTypes(this.selectedCoverage.id).then((response) => {
        const workTypes = response.data.data.map(workType => ({id: workType.id}));
        workTypes.push({id: this.workTypeToAdd.id});
        this.$api.coverage.updateWorkTypes(this.selectedCoverage.id, workTypes).then(() => {
          this.lastAdded = Object.assign({}, this.workTypeToAdd);
          this.workTypeToAdd = null;
          this.savedSuccessfully = true;
        })
          .catch(() => this.notify(this.$bvToast, this.$t('There was an error saving your new lead type'), null, 'danger'))
          .finally(() => this.savingWorkType = false);
      });
    },
    confirmDirtyForm: function() {
      return !this.disableSave && !window.confirm(this.$t('You have unsaved changes, are you sure you want to leave?'));
    },
    beforeWindowUnload: function(e) {
      if (this.confirmDirtyForm()) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
  },
  created: function() {
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  },
  destroyed: function() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
}
</script>

<style scoped lang="scss">
.lead-types {
  top: 100px;
  &-save {
    .btn {
      min-width: 95px;
    }
  }
}

.worktype {
  li::before {
    content: "\2022";
    color: $theme-secondary;
    font-size: 1rem;
    display: inline-block;
    width: 1rem;
  }
  &-unavailable {
    li {
      &:before {
        color: $border-color;
      }
    }
  }
}

.submit-container {
  width: 100px;
}
</style>
